import {
  List,
  Datagrid,
  TextField,
  DateField,
  FunctionField,
  FilterButton,
  SelectInput,
  TopToolbar,
  ExportButton,
  useRecordContext,
} from "react-admin";
import { Box, Chip, Tooltip, Typography } from "@mui/material";
import { 
  ShieldAlert, 
  FileText, 
  AlertTriangle, 
  Bug, 
  Globe,
  CheckCircle,
  XCircle,
  LogOut,
  Clock,
  UserCog,
  MessageSquare
} from "lucide-react";

// Level choices for filtering
const levelChoices = [
  { id: "info", name: "Info" },
  { id: "warn", name: "Warning" },
  { id: "error", name: "Error" },
];

// Category choices for filtering
const categoryChoices = [
  { id: "security", name: "Security" },
  { id: "audit", name: "Audit" },
  { id: "error", name: "Error" },
  { id: "frontend", name: "Frontend" },
];

// Event choices for filtering
const eventChoices = [
  { id: "auth_success", name: "Auth Success" },
  { id: "auth_failed", name: "Auth Failed" },
  { id: "auth_logout", name: "Auth Logout" },
  { id: "rate_limit_exceeded", name: "Rate Limited" },
  { id: "admin_action", name: "Admin Action" },
  { id: "contact_request", name: "Contact Request" },
  { id: "api_error", name: "API Error" },
  { id: "frontend_error", name: "Frontend Error" },
];

// Filters for the list
const logFilters = [
  <SelectInput
    key="level"
    source="level"
    label="Level"
    choices={levelChoices}
    alwaysOn
  />,
  <SelectInput
    key="category"
    source="category"
    label="Category"
    choices={categoryChoices}
  />,
  <SelectInput
    key="event"
    source="event"
    label="Event"
    choices={eventChoices}
  />,
];

// Custom level field with color coding
const LevelField = () => {
  const record = useRecordContext();
  if (!record) return null;

  const levelConfig: Record<string, { color: "error" | "warning" | "info"; icon: React.ReactNode }> = {
    error: { color: "error", icon: <Bug size={14} /> },
    warn: { color: "warning", icon: <AlertTriangle size={14} /> },
    info: { color: "info", icon: <CheckCircle size={14} /> },
  };

  const config = levelConfig[record.level] || levelConfig.info;

  return (
    <Chip
      icon={config.icon as React.ReactElement}
      label={record.level.toUpperCase()}
      size="small"
      color={config.color}
      variant="outlined"
    />
  );
};

// Custom category field with icon
const CategoryField = () => {
  const record = useRecordContext();
  if (!record?.details) return null;

  const category = (record.details as any)?.category;
  const categoryIcons: Record<string, React.ReactNode> = {
    security: <ShieldAlert size={14} />,
    audit: <FileText size={14} />,
    error: <Bug size={14} />,
    frontend: <Globe size={14} />,
  };

  const categoryColors: Record<string, "primary" | "secondary" | "error" | "warning"> = {
    security: "warning",
    audit: "primary",
    error: "error",
    frontend: "secondary",
  };

  return (
    <Chip
      icon={categoryIcons[category] as React.ReactElement}
      label={category || "—"}
      size="small"
      color={categoryColors[category] || "primary"}
      variant="outlined"
    />
  );
};

// Custom event field with icon
const EventField = () => {
  const record = useRecordContext();
  if (!record?.details) return null;

  const event = (record.details as any)?.event;
  const eventIcons: Record<string, React.ReactNode> = {
    auth_success: <CheckCircle size={14} color="#22c55e" />,
    auth_failed: <XCircle size={14} color="#ef4444" />,
    auth_logout: <LogOut size={14} />,
    rate_limit_exceeded: <Clock size={14} color="#f59e0b" />,
    admin_action: <UserCog size={14} color="#22d3ee" />,
    contact_request: <MessageSquare size={14} color="#a855f7" />,
    api_error: <Bug size={14} color="#ef4444" />,
    frontend_error: <Globe size={14} color="#ef4444" />,
  };

  const eventLabels: Record<string, string> = {
    auth_success: "Login",
    auth_failed: "Login Failed",
    auth_logout: "Logout",
    rate_limit_exceeded: "Rate Limited",
    admin_action: "Admin Action",
    contact_request: "Contact",
    api_error: "API Error",
    frontend_error: "Frontend Error",
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {eventIcons[event] || null}
      <Typography variant="body2">{eventLabels[event] || event}</Typography>
    </Box>
  );
};

// Custom details field (shows message or action)
const DetailsField = () => {
  const record = useRecordContext();
  if (!record?.details) return null;

  const details = record.details as any;
  const message = details.message || details.action || "—";
  
  // Truncate long messages
  const truncated = message.length > 60 ? message.slice(0, 60) + "..." : message;

  return (
    <Tooltip title={message} placement="top">
      <Typography 
        variant="body2" 
        sx={{ 
          maxWidth: 300, 
          overflow: "hidden", 
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {truncated}
      </Typography>
    </Tooltip>
  );
};

// Custom IP field
const IpField = () => {
  const record = useRecordContext();
  if (!record?.details) return null;

  const ip = (record.details as any)?.ip;
  return (
    <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}>
      {ip || "—"}
    </Typography>
  );
};

// List actions toolbar
const ListActions = () => (
  <TopToolbar>
    <FilterButton />
    <ExportButton />
  </TopToolbar>
);

/**
 * SystemLogsList - Displays all system logs in a datagrid with filters
 * Read-only view of security and audit events from the database
 */
export const SystemLogsList = () => {
  return (
    <List
      filters={logFilters}
      actions={<ListActions />}
      sort={{ field: "timestamp", order: "DESC" }}
      perPage={50}
      sx={{
        "& .RaList-main": {
          backgroundColor: "#1a1a2e",
        },
      }}
    >
      <Datagrid
        bulkActionButtons={false}
        sx={{
          "& .RaDatagrid-headerCell": {
            backgroundColor: "#16213e",
            color: "#22d3ee",
            fontWeight: 600,
          },
          "& .RaDatagrid-row": {
            "&:hover": {
              backgroundColor: "rgba(34, 211, 238, 0.05)",
            },
          },
        }}
      >
        <DateField source="timestamp" label="Time" showTime />
        <LevelField />
        <CategoryField />
        <EventField />
        <DetailsField />
        <IpField />
      </Datagrid>
    </List>
  );
};

export default SystemLogsList;

